import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { openLink } from '@tma.js/sdk-react';
import { Loader2, Swords, Trophy, UserPlus, X } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useCountdown } from '@/hooks/useCountdown';
import {
  useQueueEnterMutation,
  useQueueLeaveMutation,
  useQueueStatusQuery,
  useCheckInQueueMutation,
} from '@/services/qduelsApi';
import { classifyQduelsQueueError, type QduelsQueueErrorKind } from '@/services/qduelsErrors';
import { Sheet } from '@/components/common/Sheet';
import { cn } from '@/lib/cn';

/** Auto-stop the search after this long with no opponent (spec default 3 min). */
const QUEUE_TIMEOUT_MS = 180_000;
/** Poll `GET /v1/queue/status` cadence while the sheet is searching. */
const QUEUE_POLL_MS = 3_000;
/** Canonical Clash-Royale open handoff (same target as MatchDetail). */
const CLASH_ROYALE_LINK = 'https://link.clashroyale.com';

interface QueueSheetProps {
  /** Render + drive the queue only while true. Parent mounts it on open so the
   *  internal state machine resets cleanly on every search. */
  open: boolean;
  /** Close handler — the sheet has already called queueLeave by the time it
   *  invokes this, so the parent only has to drop `open`. */
  onClose: () => void;
}

/**
 * The sheet's local lifecycle:
 *  - `entering`   — queueEnter in flight (initial + Riprova).
 *  - `searching`  — in queue, polling status, waiting for a pairing.
 *  - `checkin`    — paired, gate open (status.checkin != null): confirm you're
 *                   here before the invite is created. Only occurs when qduels'
 *                   FEATURE_MATCH_CHECKIN is on; otherwise pairing goes straight
 *                   to `found`.
 *  - `found`      — status.currentMatch != null → "Avversario trovato!".
 *  - `timeout`    — elapsed >= QUEUE_TIMEOUT_MS with no match (queueLeave done).
 *  - `precheck`   — queueEnter rejected (not-linked / not-eligible / in-match).
 */
type Phase = 'entering' | 'searching' | 'checkin' | 'found' | 'timeout' | 'precheck' | 'dropped';

/**
 * How many consecutive polls must report "not in the queue" before we believe it.
 *
 * A SUCCESSFUL pairing also sets `isSearching: false` (the server writes it
 * alongside `currentPairId`), so a naive `!isSearching → dropped` would tell a
 * freshly-matched player they left the queue. `currentMatch` is derived from a
 * separate lookup, so a poll landing between the pairing write and the match
 * becoming visible could momentarily read as (!isSearching, !currentMatch).
 * Two consecutive reads (~6s) cover that window; the found-check runs first, so
 * pairing always wins.
 */
const DROP_CONFIRM_POLLS = 2;

/** mm:ss for the count-up timer. */
function formatElapsed(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Open-matchmaking bottom sheet. Mirrors ChallengeOptionsSheet's shell. On open
 * it enters the queue (fixed pool: REGULAR / bo1), polls status every ~3s, and
 * shows searching / found / not-found. ALWAYS calls queueLeave on cancel /
 * timeout / close to avoid ghost queue entries (backend has no TTL yet).
 */
export function QueueSheet({ open, onClose }: QueueSheetProps) {
  const { t } = useTranslation('duels');
  const haptics = useHaptics();
  const navigate = useNavigate();

  const [queueEnter] = useQueueEnterMutation();
  const [queueLeave] = useQueueLeaveMutation();
  const [checkInQueue, checkInState] = useCheckInQueueMutation();

  const [phase, setPhase] = useState<Phase>('entering');
  const [precheck, setPrecheck] = useState<QduelsQueueErrorKind | null>(null);
  const [startedAt, setStartedAt] = useState<number>(() => Date.now());
  const [elapsed, setElapsed] = useState(0);

  // Poll status while searching AND after a match is found, so we can detect the
  // moment the paired match reaches `complete` and surface the result CTA. RTK
  // Query keeps polling as long as the hook stays mounted with a pollingInterval
  // AND skip=false.
  const searching = phase === 'searching';
  const checkin = phase === 'checkin';
  const found = phase === 'found';
  const statusQuery = useQueueStatusQuery(undefined, {
    // Keep polling through `checkin` too: it's how we learn the opponent
    // confirmed (→ currentMatch appears → `found`).
    skip: !open || (!searching && !checkin && !found),
    pollingInterval: QUEUE_POLL_MS,
    // GET /v1/queue/status is not a read: it IS the queue's liveness heartbeat
    // (it writes `lastSeen`, and a reaper drops anyone staler than the TTL).
    // On resume, read it immediately instead of waiting for the next tick — the
    // sooner we learn we were reaped, the sooner we can say so.
    refetchOnFocus: true,
  });
  const status = statusQuery.data;
  const matchComplete = status?.currentMatch?.state === 'complete';
  const resultGameTag = status?.currentMatch?.gameTag;
  const iCheckedIn = Boolean(status?.checkin?.checkedIn);
  const checkinCountdown = useCountdown(checkin ? status?.checkin?.deadlineAt : null);

  // Guard against double-leave (cancel then unmount, timeout then close, …) so
  // we never fire a redundant queueLeave. Reset whenever the sheet (re)opens.
  const leftRef = useRef(false);

  // Guard so `enter` fires EXACTLY ONCE per sheet-open. The sheet is freshly
  // mounted by the parent on every open (`{queueOpen && <QueueSheet .../>}`),
  // so this ref starts false on each open and flips true on the first enter.
  // Without it the open-effect could re-fire (e.g. if `enter`'s identity ever
  // changed) and spam POST /v1/queue/enter — the original PROD bug.
  const enteredRef = useRef(false);

  const leaveQueue = useCallback(() => {
    if (leftRef.current) return;
    leftRef.current = true;
    // Fire-and-forget — closing the UI must not wait on the network, and a
    // failed leave is non-fatal (worst case a stale entry the backend TTL
    // follow-up will sweep). The catch keeps it from becoming an unhandled
    // rejection.
    queueLeave()
      .unwrap()
      .catch(() => {
        /* best-effort cleanup */
      });
  }, [queueLeave]);

  // Enter (or re-enter) the queue. Used on open (via the guarded effect below)
  // and on Riprova. On failure it lands in a TERMINAL `precheck` phase — it does
  // NOT retry, so a 403 not_eligible produces exactly one call, not a loop.
  const enter = useCallback(async () => {
    leftRef.current = false;
    setPrecheck(null);
    setPhase('entering');
    setStartedAt(Date.now());
    setElapsed(0);
    try {
      await queueEnter({ gameMode: 'REGULAR', format: 'bo1' }).unwrap();
      setPhase('searching');
    } catch (err) {
      const kind = classifyQduelsQueueError(err);
      haptics.notification('error');
      setPrecheck(kind);
      setPhase('precheck');
    }
  }, [queueEnter, haptics]);

  // Keep a ref to the latest `enter` so the open-effect can call it WITHOUT
  // listing `enter` (or its transitive deps like `haptics`) as a dependency.
  // This is what makes the open-effect immune to identity churn: it can only
  // fire on an actual open transition, never on a re-render / state / error /
  // status-poll change.
  const enterRef = useRef(enter);
  enterRef.current = enter;

  // Enter the queue EXACTLY ONCE per open. Deps are `[open]` only; the
  // `enteredRef` guard makes it idempotent even if React were to re-run the
  // effect, and it resets on close so the next open enters cleanly.
  useEffect(() => {
    if (!open) {
      enteredRef.current = false;
      return;
    }
    if (enteredRef.current) return;
    enteredRef.current = true;
    void enterRef.current();
  }, [open]);

  // Count-up timer + timeout watchdog, only while searching.
  useEffect(() => {
    if (!open || !searching) return;
    const id = window.setInterval(() => {
      const next = Date.now() - startedAt;
      setElapsed(next);
      if (next >= QUEUE_TIMEOUT_MS) {
        leaveQueue();
        haptics.notification('warning');
        setPhase('timeout');
      }
    }, 1_000);
    return () => window.clearInterval(id);
  }, [open, searching, startedAt, leaveQueue, haptics]);

  // Paired, gate open → move to the check-in step (the "Play" button). Only
  // happens when qduels' check-in gate is on; otherwise `checkin` stays null and
  // pairing goes straight to `found` below.
  useEffect(() => {
    if (searching && status?.checkin && !status.currentMatch) {
      haptics.notification('success');
      setPhase('checkin');
    }
  }, [searching, status?.checkin, status?.currentMatch, haptics]);

  // Transition to "found" the moment the poll reports an assigned match — from
  // `searching` (gate off) OR `checkin` (both confirmed → the match now exists).
  useEffect(() => {
    if ((searching || checkin) && status?.currentMatch) {
      haptics.notification('success');
      setPhase('found');
    }
  }, [searching, checkin, status?.currentMatch, haptics]);

  // The gate closed without a match: I (or my opponent) let the window lapse, so
  // the server resolved the pair — I'm out, possibly on a cooldown. Without this
  // the sheet would sit on a "Play" button whose countdown reads 0:00 and whose
  // tap just 410s into the silent catch. `found` wins if a match DID appear (the
  // effect above runs first and flips the phase), so this only fires on a real
  // lapse, not on the both-confirmed → match handoff.
  useEffect(() => {
    if (checkin && status !== undefined && !status.checkin && !status.currentMatch) {
      haptics.notification('warning');
      setPhase('dropped');
    }
  }, [checkin, status, haptics]);

  /**
   * Detect that the server dropped us from the queue.
   *
   * The queue's liveness IS this poll (`status` writes `lastSeen`; a reaper
   * flips `isSearching:false` past the TTL, and the heartbeat is filtered on
   * `isSearching:true`, so it never re-enters us). Until now we ignored
   * `isSearching` entirely and kept spinning "Searching for an opponent…" while
   * being in no queue at all, until the unrelated 3-minute watchdog fired
   * "No opponent found" — the wrong explanation.
   *
   * Anything that stops the poll for longer than the TTL does this: the app
   * being backgrounded, or simply a sustained poll failure (a 401 after the
   * token expires mid-search is the live case, and `retryCondition` refuses to
   * retry 4xx) — which happens while the user is watching the spinner.
   *
   * We do NOT auto re-enter: the reaper exists so a dead client is never paired
   * into a phantom match. Getting back in must be a deliberate tap.
   */
  const dropStreak = useRef(0);
  useEffect(() => {
    if (!searching) {
      dropStreak.current = 0;
      return;
    }
    // A poll error stops the heartbeat just as effectively as a "not searching"
    // answer — count both. (No data + no error = first load, not a drop.)
    //
    // ⚠️ `!status.checkin` is the fix: a successful PAIRING also sets
    // `isSearching:false` (and currentMatch is still null until both confirm),
    // so without this the check-in step reads exactly like a drop and the user
    // is falsely told "you left the queue" while actually paired and needing to
    // confirm. The check-in detector above owns that state.
    const reportsDropped =
      status !== undefined && !status.isSearching && !status.currentMatch && !status.checkin;
    const pollFailing = statusQuery.isError;
    if (!reportsDropped && !pollFailing) {
      dropStreak.current = 0;
      return;
    }
    dropStreak.current += 1;
    if (dropStreak.current >= DROP_CONFIRM_POLLS) {
      dropStreak.current = 0;
      haptics.notification('warning');
      setPhase('dropped');
    }
  }, [searching, status, statusQuery.isError, statusQuery.fulfilledTimeStamp, haptics]);

  // Safety net: if the sheet unmounts while still in the queue (app close,
  // route change), leave the queue.
  //
  // 🔴 «WHILE STILL IN THE QUEUE» non era verificato, e il commento lo dava per fatto.
  // Misurato da or45 su `stg-tma` il 2026-09-06: aprendo EVENTI o LIVE partiva una
  // `POST /v1/queue/leave` che nessuno aveva chiesto (401 su questo tenant). La causa:
  // `<RankedPanel>` tiene il foglio SEMPRE montato — di proposito, per poterlo far
  // scivolare via — quindi cambiare scheda nella Hub lo smonta, e la pulizia partiva
  // anche per chi in coda non c'era MAI entrato.
  //
  // ⛔ Un `leave` senza `enter` non e' una pulizia: e' una SCRITTURA non sollecitata verso
  // un servizio che nessuno ha interrogato. Oggi e' innocua perche' risponde 401; il
  // giorno che risponde 200 non lo e' piu', e nessuno si ricordera' di questo effetto.
  // `enteredRef` e' gia' la memoria di «siamo entrati davvero» e si azzera alla chiusura.
  useEffect(() => {
    return () => {
      if (!enteredRef.current) return;
      leaveQueue();
    };
  }, [leaveQueue]);

  // Niente `if (!open) return null` qui: la visibilità la decide `Sheet`, che
  // deve restare montato per la durata dell'uscita. Uscendo di qui, il pannello
  // sparirebbe prima di poter scivolare via — che è com'era prima.
  // Montarlo a pannello chiuso non costa nulla: la richiesta di stato e i due
  // effetti che contano sono già tutti condizionati su `open`.

  const close = () => {
    leaveQueue();
    onClose();
  };

  const openClashRoyale = () => {
    haptics.impact('medium');
    openLink.ifAvailable(CLASH_ROYALE_LINK);
    onClose();
  };

  const onCheckInQueue = async () => {
    haptics.impact('medium');
    // Idempotent server-side — a double tap is a 200, no error branch needed.
    // On failure we stay in `checkin`; the poll keeps the deadline visible and
    // the reaper resolves a genuinely-gone pair. (No error toast in this sheet:
    // the poll is the source of truth, and a transient blip must not derail it.)
    await checkInQueue()
      .unwrap()
      .catch(() => {
        /* best-effort; the poll reflects the real state */
      });
  };

  const viewResult = () => {
    if (!resultGameTag) return;
    haptics.impact('medium');
    leaveQueue();
    onClose();
    navigate(`/duels/result/${encodeURIComponent(resultGameTag)}`);
  };

  const goLink = () => {
    haptics.impact('light');
    onClose();
    navigate('/duels/link');
  };

  const goChallengeFriend = () => {
    haptics.impact('light');
    leaveQueue();
    onClose();
    navigate('/friends?tab=duels');
  };

  const retry = () => {
    haptics.impact('medium');
    void enter();
  };

  // Surface the "Sfida un amico" CTA early when the caller is alone in the pool.
  const aloneInQueue = (status?.searching ?? 0) <= 1;

  return (
    // Il foglio CONDIVISO invece di un velo e un pannello riscritti qui: da
    // questa parte arrivano gratis l'entrata, l'uscita dolce, il trascinamento
    // per chiudere e il tasto indietro. Prima questo pannello compariva e
    // spariva di colpo, ed era anche l'unico che non si potesse trascinare via —
    // cioè insegnava un gesto diverso sulla stessa forma (ADR-0038, punto 7).
    <Sheet open={open} onClose={onClose} label={t('queue.find', { defaultValue: 'Find match' })}>
      <div className="px-4 pb-2 pt-1">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-bold text-fg">
            <Swords className="h-4 w-4 text-brand" />
            {t('queue.find', { defaultValue: 'Find match' })}
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label={t('queue.cancel', { defaultValue: 'Cancel' })}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted active:opacity-80"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── searching / entering ─────────────────────────────────────── */}
        {(phase === 'entering' || phase === 'searching') && (
          <div className="flex flex-col items-center gap-4 py-4">
            <Loader2 className="h-10 w-10 animate-spin text-brand" />
            <div className="text-center">
              <p className="text-sm font-semibold text-fg">
                {t('queue.searching', {
                  defaultValue: 'Searching for an opponent…',
                })}
              </p>
              <p className="mt-1 text-xs text-fg-muted">
                {t('queue.inQueue', {
                  n: status?.searching ?? 1,
                  defaultValue: '{{n}} in queue',
                })}
                {' · '}
                {formatElapsed(elapsed)}
              </p>
            </div>

            <button
              type="button"
              onClick={close}
              className="w-full rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
            >
              {t('queue.cancel', { defaultValue: 'Cancel' })}
            </button>

            {phase === 'searching' && aloneInQueue && (
              <button
                type="button"
                onClick={goChallengeFriend}
                className="flex w-full items-center justify-center gap-2 text-xs font-semibold text-brand active:opacity-80"
              >
                <UserPlus className="h-4 w-4" />
                {t('queue.challengeFriend', {
                  defaultValue: 'Challenge a friend',
                })}
              </button>
            )}
          </div>
        )}

        {/* ── check-in: paired, confirm before the invite is created ─────── */}
        {phase === 'checkin' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/15 text-brand">
              <Swords className="h-6 w-6" />
            </span>
            <p className="text-base font-bold text-fg">
              {t('queue.checkin.paired', { defaultValue: 'Opponent found!' })}
            </p>
            {iCheckedIn ? (
              <>
                <div className="flex w-full items-center justify-center gap-2 rounded-xl bg-success/15 py-3 text-sm font-bold text-success">
                  <span aria-hidden>✓</span>
                  {t('queue.checkin.confirmed', { defaultValue: "You're in" })}
                  {checkinCountdown && (
                    <span className="font-normal opacity-70">· {checkinCountdown}</span>
                  )}
                </div>
                <p className="text-center text-xs text-fg-muted">
                  {t('queue.checkin.waitingOpponent', {
                    defaultValue: 'Waiting for your opponent to confirm…',
                  })}
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onCheckInQueue}
                  disabled={checkInState.isLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3.5 text-base font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-60"
                >
                  <Swords className="h-5 w-5" />
                  {checkinCountdown
                    ? t('queue.checkin.playIn', {
                        defaultValue: 'Play · {{time}}',
                        time: checkinCountdown,
                      })
                    : t('queue.checkin.play', { defaultValue: 'Play' })}
                </button>
                <p className="text-center text-xs text-fg-muted">
                  {t('queue.checkin.hint', {
                    defaultValue: "Confirm you're here. Miss the window and you forfeit.",
                  })}
                </p>
              </>
            )}
          </div>
        )}

        {/* ── found ────────────────────────────────────────────────────── */}
        {phase === 'found' && (
          <div className="flex flex-col items-center gap-4 py-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/15 text-brand">
              <Swords className="h-6 w-6" />
            </span>
            <p className="text-base font-bold text-fg">
              {matchComplete
                ? t('queue.matchComplete', { defaultValue: 'Match complete!' })
                : t('queue.found', { defaultValue: 'Opponent found!' })}
            </p>
            {matchComplete && resultGameTag ? (
              <button
                type="button"
                onClick={viewResult}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
              >
                <Trophy className="h-4 w-4" />
                {t('queue.viewResult', { defaultValue: 'View result' })}
              </button>
            ) : (
              <button
                type="button"
                onClick={openClashRoyale}
                // Clash Royale's blue — same deliberate off-palette exception as
                // the tournament flow: this button hands off to their app.
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1c6dfb] py-3 text-sm font-bold text-white transition-opacity active:opacity-80"
              >
                <Swords className="h-4 w-4" />
                {t('queue.openCR', { defaultValue: 'Open Clash Royale' })}
              </button>
            )}
          </div>
        )}

        {/* ── timeout / not-found ──────────────────────────────────────── */}
        {phase === 'timeout' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <p className="text-sm font-semibold text-fg">
              {t('queue.notFound', { defaultValue: 'No opponent found' })}
            </p>
            <button
              type="button"
              onClick={retry}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
            >
              {t('queue.retry', { defaultValue: 'Try again' })}
            </button>
            <button
              type="button"
              onClick={goChallengeFriend}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
            >
              <UserPlus className="h-4 w-4" />
              {t('queue.challengeFriend', { defaultValue: 'Challenge a friend' })}
            </button>
          </div>
        )}

        {/* ── dropped: the server took us out of the queue ─────────────── */}
        {phase === 'dropped' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <p className="text-sm font-semibold text-fg">
              {t('queue.dropped', { defaultValue: 'Search stopped' })}
            </p>
            {/* Say WHY. The old behaviour ended at "No opponent found", which
                blamed the matchmaking for something the client caused. */}
            <p className="text-center text-xs text-fg-muted">
              {t('queue.droppedHint', {
                defaultValue:
                  'You left the queue — the app was away or lost connection. Tap to search again.',
              })}
            </p>
            <button
              type="button"
              onClick={retry}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
            >
              {t('queue.retry', { defaultValue: 'Try again' })}
            </button>
            <button
              type="button"
              onClick={goChallengeFriend}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
            >
              <UserPlus className="h-4 w-4" />
              {t('queue.challengeFriend', { defaultValue: 'Challenge a friend' })}
            </button>
          </div>
        )}

        {/* ── pre-check error ──────────────────────────────────────────── */}
        {phase === 'precheck' && (
          <QueuePrecheck
            kind={precheck}
            onLink={goLink}
            onOpenCR={openClashRoyale}
            onClose={close}
          />
        )}
      </div>
    </Sheet>
  );
}

/** The pre-check / enter-error body, split out to keep the state machine tidy. */
function QueuePrecheck({
  kind,
  onLink,
  onOpenCR,
  onClose,
}: {
  kind: QduelsQueueErrorKind | null;
  onLink: () => void;
  onOpenCR: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('duels');

  if (kind === 'player_not_linked') {
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <p className="text-center text-sm font-semibold text-fg">
          {t('subtitle', {
            defaultValue:
              'Link your Clash Royale account to join tournaments, challenge friends to duels, and see your stats.',
          })}
        </p>
        <button type="button" onClick={onLink} className={primaryBtn}>
          {t('queue.linkAccount', { defaultValue: 'Link account' })}
        </button>
      </div>
    );
  }

  if (kind === 'not_eligible') {
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <p className="text-center text-sm font-semibold text-fg">
          {t('queue.notEligible', {
            defaultValue: 'You need ≥32 cards and level ≥10',
          })}
        </p>
        <button type="button" onClick={onClose} className={secondaryBtn}>
          {t('queue.cancel', { defaultValue: 'Cancel' })}
        </button>
      </div>
    );
  }

  if (kind === 'already_in_match') {
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <p className="text-center text-sm font-semibold text-fg">
          {t('queue.alreadyInMatch', {
            defaultValue: 'You already have a match in progress',
          })}
        </p>
        <button type="button" onClick={onOpenCR} className={primaryBtn}>
          {t('queue.openCR', { defaultValue: 'Open Clash Royale' })}
        </button>
      </div>
    );
  }

  // auth / network / server / unknown — generic copy.
  return (
    <div className="flex flex-col items-center gap-3 py-4">
      <p className="text-center text-sm font-semibold text-fg">
        {t('queue.error', { defaultValue: 'Could not join the queue. Try again.' })}
      </p>
      <button type="button" onClick={onClose} className={secondaryBtn}>
        {t('queue.cancel', { defaultValue: 'Cancel' })}
      </button>
    </div>
  );
}

const primaryBtn = cn(
  'flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3',
  'text-sm font-bold text-brand-fg transition-opacity active:opacity-80',
);
const secondaryBtn = cn(
  'flex w-full items-center justify-center gap-2 rounded-xl bg-bg-tertiary py-3',
  'text-sm font-bold text-fg-muted transition-opacity active:opacity-80',
);
